import { Info } from "@mui/icons-material";
import { Box, Chip, TextField, Typography } from "@mui/material";
import useColors from "../../../api/hooks/useColors";

const FilterDisplayBox = ({ filters, isEditMode = false }) => {
  const colors = useColors();

  // Map technical filter names to human-readable names
  const getHumanReadableFilterName = (technicalName) => {
    // Handle camelCase and PascalCase conversion
    let readableName = technicalName
      .replace("_", " ") // Replace underscores with spaces
      .replace(/([A-Z])/g, " $1") // Add space before capital letters
      .replace(/^./, (str) => str.toUpperCase()) // Capitalize first letter of each word
      .trim(); // Remove any leading/trailing spaces

    return readableName;
  };

  return (
    <Box>
      <TextField
        fullWidth
        variant="outlined"
        label="Filters"
        sx={{
          "& .MuiOutlinedInput-root": {
            backgroundColor: colors.grey_50,
            minHeight: "44px",
            padding: "14px 14px 0px 14px",
            borderRadius: "8px",
            "& fieldset": {
              border: `1px solid ${colors.grey_300}`,
              borderRadius: "8px",
            },
            "&:hover fieldset": {
              border: `1px solid ${colors.grey_300}`,
            },
            cursor: "default",
          },
          "& .MuiInputLabel-root": {
            color: colors.text_disabled,
            fontSize: "16px",
            fontFamily: "Poppins, sans-serif",
          },
          "& .MuiInputBase-root": {
            display: "flex",
            flexWrap: "wrap",
            gap: "8px",
            alignItems: "center",
            padding: "14px",
            cursor: "default",
          },
        }}
        InputProps={{
          readOnly: true,
          startAdornment: (
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: "8px",
                alignItems: "center",
                flex: 1,
              }}
            >
              {filters &&
                Object.entries(filters)
                  .filter(([key, value]) => {
                    // Only show filters that have meaningful values
                    if (
                      value === null ||
                      value === undefined ||
                      value === "" ||
                      value === false
                    )
                      return false;

                    if (typeof value === "object") {
                      // For objects, only show if they have meaningful properties
                      if (Array.isArray(value)) {
                        // Handle arrays - show if not empty
                        return value.length > 0;
                      }

                      const meaningfulEntries = Object.entries(value).filter(
                        ([k, v]) =>
                          v !== null &&
                          v !== undefined &&
                          v !== "" &&
                          v !== false
                      );
                      return meaningfulEntries.length > 0;
                    }

                    return true;
                  })
                  .map(([key, value]) => {
                    // Format the value display
                    let displayValue = value;
                    let chipLabel = key;

                    if (typeof value === "object") {
                      // Handle objects differently based on their structure
                      const entries = Object.entries(value).filter(
                        ([k, v]) => v !== null && v !== undefined && v !== ""
                      );

                      if (entries.length === 0) {
                        // Empty object - skip entirely
                        return null;
                      } else if (entries.length === 1) {
                        // Single entry - show just the value if it's meaningful
                        const [objKey, objValue] = entries[0];
                        if (typeof objValue === "boolean") {
                          // For booleans, just show the key if true
                          if (objValue === true) {
                            displayValue = getHumanReadableFilterName(objKey);
                            chipLabel = `${getHumanReadableFilterName(key)}`;
                          } else {
                            return null; // Skip false booleans
                          }
                        } else if (Array.isArray(objValue)) {
                          // Handle arrays - check for "All" selection
                          const hasAll = objValue.some(
                            (val) =>
                              String(val).toLowerCase() === "all" ||
                              String(val).toLowerCase() === "select all" ||
                              String(val).toLowerCase() === "all tools"
                          );

                          if (hasAll) {
                            displayValue = "All";
                          } else {
                            const readableValues = objValue.map((val) =>
                              getHumanReadableFilterName(String(val))
                            );
                            displayValue = readableValues.join(", ");
                          }

                          chipLabel = `${getHumanReadableFilterName(
                            key
                          )}: ${getHumanReadableFilterName(objKey)}`;
                        } else {
                          displayValue = objValue;
                          chipLabel = `${getHumanReadableFilterName(
                            key
                          )}: ${getHumanReadableFilterName(objKey)}`;
                        }
                      } else {
                        // Multiple entries - check if 'All' is selected
                        const hasAll = entries.some(
                          ([k, v]) =>
                            String(k).toLowerCase() === "all" && v === true
                        );
                        if (hasAll) {
                          displayValue = "All";
                          chipLabel = getHumanReadableFilterName(key);
                        } else {
                          // Show all selected values
                          const formattedEntries = entries
                            .map(([k, v]) => {
                              if (typeof v === "boolean") {
                                return v ? getHumanReadableFilterName(k) : null;
                              }
                              return `${getHumanReadableFilterName(k)}: ${v}`;
                            })
                            .filter(Boolean)
                            .join(", ");
                          displayValue = formattedEntries;
                          chipLabel = getHumanReadableFilterName(key);
                        }
                      }
                    } else if (Array.isArray(value)) {
                      // Handle arrays - show all non-empty values
                      const meaningfulValues = value.filter(
                        (item) =>
                          item !== null && item !== undefined && item !== ""
                      );
                      if (meaningfulValues.length === 0) {
                        return null; // Skip empty arrays
                      }

                      // Check if "All" is selected
                      const hasAll = meaningfulValues.some(
                        (val) =>
                          String(val).toLowerCase() === "all" ||
                          String(val).toLowerCase() === "select all" ||
                          String(val).toLowerCase() === "all tools"
                      );

                      if (hasAll) {
                        displayValue = "All";
                      } else {
                        const readableValues = meaningfulValues.map((val) =>
                          getHumanReadableFilterName(String(val))
                        );
                        displayValue = readableValues.join(", ");
                      }

                      chipLabel = getHumanReadableFilterName(key);
                    } else {
                      // Handle primitive values
                      if (typeof value === "boolean") {
                        if (value === true) {
                          displayValue = "Yes";
                          chipLabel = getHumanReadableFilterName(key);
                        } else {
                          return null; // Skip false booleans
                        }
                      } else {
                        displayValue = value;
                        chipLabel = getHumanReadableFilterName(key);
                      }
                    }

                    return (
                      <Chip
                        key={`${key}-${displayValue}`}
                        sx={{
                          backgroundColor: colors.grey_50,
                          border: `0.5px solid ${colors.grey_300}`,
                          borderRadius: "8px",
                          height: "auto",
                          padding: "3px 5px",
                          "& .MuiChip-label": {
                            padding: 0,
                            marginRight: "-2px",
                            fontSize: "12px",
                            lineHeight: "16px",
                            fontFamily: "Poppins, sans-serif",
                            color: colors.text_disabled,
                            whiteSpace: "normal",
                          },
                        }}
                        label={
                          <span
                            style={{
                              fontSize: "12px",
                              lineHeight: "16px",
                            }}
                          >
                            <span
                              style={{
                                fontFamily: "Poppins, sans-serif",
                                fontWeight: 600,
                              }}
                            >
                              {chipLabel}
                            </span>
                            {displayValue && (
                              <span
                                style={{
                                  fontFamily: "Poppins, sans-serif",
                                  fontWeight: 400,
                                }}
                              >
                                {`: ${displayValue}`}
                              </span>
                            )}
                          </span>
                        }
                      />
                    );
                  })}
            </Box>
          ),
        }}
      />
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
          marginTop: "4px",
          paddingLeft: "12px",
        }}
      >
        <Info
          sx={{
            fontSize: "16px",
            color: colors.helper_text,
          }}
        />
        <Typography
          sx={{
            fontFamily: "Poppins, sans-serif",
            fontSize: "12px",
            fontWeight: 400,
            lineHeight: "16px",
            color: colors.helper_text,
          }}
        >
          {isEditMode
            ? "These filters are saved in this filter set."
            : "These filters will be saved in this filter set."}
        </Typography>
      </Box>
    </Box>
  );
};

export default FilterDisplayBox;
